// Copy lead(s) into one or more destination projects. Ported from CRM_NEW_2.0.
// Single-mode (`lead` prop) and bulk-mode (`leadIds` prop) share the same
// destination picker and duplicate-resolution UX.
//
// The server's single-copy endpoint is one-target-at-a-time; this component
// iterates over targets client-side so the modal can offer multi-destination
// picking without a separate fan-out endpoint. Duplicates from any target are
// accumulated and prompted in one consolidated step.

import React, { useMemo, useState } from "react";
import { Copy, Loader2, AlertTriangle, Search, Check, X } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "../../overlays/Modal";
import { Button } from "../../atoms";
import { useProjects } from "../../hooks/useProjects";
import { useCopyLead, useBulkCopyLeads } from "../../hooks/useLeads";

export default function CopyLeadModal({
  open,
  onClose,
  lead,         // single-mode
  leadIds,      // bulk-mode
  onCopied,
}) {
  const isBulk = Array.isArray(leadIds) && leadIds.length > 0;
  const sourceId = lead?.projectId || null;

  const { projects, isLoading: projectsLoading } = useProjects();
  const copyOne = useCopyLead();
  const copyMany = useBulkCopyLeads();

  const [targetIds, setTargetIds] = useState([]);
  const [q, setQ] = useState("");
  const [isRunning, setIsRunning] = useState(false);

  const [pendingSingleDups, setPendingSingleDups] = useState([]); // [{ targetProjectId, name, existingLead }]
  const [pendingBulkDups, setPendingBulkDups] = useState([]);     // [{ targetProjectId, name, duplicates }]
  const [accCopied, setAccCopied] = useState(0);
  const [accFailed, setAccFailed] = useState(0);

  const projectOptions = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (projects || [])
      .filter((p) => !sourceId || String(p._id) !== String(sourceId))
      .filter((p) => !needle || (p.name || "").toLowerCase().includes(needle));
  }, [projects, sourceId, q]);

  const projectNameById = useMemo(() => {
    const m = new Map();
    for (const p of projects || []) m.set(String(p._id), p.name);
    return m;
  }, [projects]);

  const reset = () => {
    setTargetIds([]);
    setQ("");
    setPendingSingleDups([]);
    setPendingBulkDups([]);
    setAccCopied(0);
    setAccFailed(0);
    setIsRunning(false);
  };

  const isPending = isRunning || copyOne.isPending || copyMany.isPending;
  const showDupPrompt = pendingSingleDups.length > 0 || pendingBulkDups.length > 0;

  const handleClose = () => {
    if (isPending) return;
    reset();
    onClose?.();
  };

  const finish = (copied, failed) => {
    if (copied > 0) toast.success(`Copied to ${copied} project${copied > 1 ? "s" : ""}`);
    if (failed > 0) toast.error(`${failed} copy operation${failed > 1 ? "s" : ""} failed`);
    onCopied?.();
    reset();
    onClose?.();
  };

  const toggleTarget = (id) => {
    setTargetIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return [...s];
    });
  };

  // ---------- single-lead, multi-target ----------
  const runSingleForTargets = async (targets, confirmDuplicate) => {
    let copied = 0;
    let failed = 0;
    const dups = [];
    for (const t of targets) {
      try {
        await copyOne.mutateAsync({ leadId: lead._id, targetProjectId: t, confirmDuplicate });
        copied++;
      } catch (err) {
        if (err?.code === "DUPLICATE_IN_TARGET") {
          dups.push({
            targetProjectId: t,
            name: projectNameById.get(t) || "destination project",
            existingLead: err.existingLead || null,
          });
        } else {
          failed++;
        }
      }
    }
    return { copied, failed, dups };
  };

  const handleSubmitSingle = async () => {
    if (targetIds.length === 0) return;
    setIsRunning(true);
    const r = await runSingleForTargets(targetIds, false);
    setIsRunning(false);
    if (r.dups.length > 0) {
      setAccCopied(r.copied);
      setAccFailed(r.failed);
      setPendingSingleDups(r.dups);
      return;
    }
    finish(r.copied, r.failed);
  };

  const handleConfirmSingleDups = async () => {
    setIsRunning(true);
    const r = await runSingleForTargets(pendingSingleDups.map((d) => d.targetProjectId), true);
    setIsRunning(false);
    finish(accCopied + r.copied, accFailed + r.failed);
  };

  const handleSkipSingleDups = () => finish(accCopied, accFailed + pendingSingleDups.length);

  // ---------- bulk leads, multi-target ----------
  const runBulkForTargets = async (targets, { skipDuplicates = false, copyDuplicates = false } = {}) => {
    let copied = 0;
    let failed = 0;
    const dupsPerTarget = [];
    for (const t of targets) {
      try {
        const r = await copyMany.mutateAsync({ leadIds, targetProjectId: t, skipDuplicates, copyDuplicates });
        copied += r.copied?.length || 0;
        failed += r.failed?.length || 0;
        if (r.duplicates?.length > 0 && !skipDuplicates && !copyDuplicates) {
          dupsPerTarget.push({
            targetProjectId: t,
            name: projectNameById.get(t) || "destination project",
            duplicates: r.duplicates,
          });
        }
      } catch {
        failed += leadIds.length;
      }
    }
    return { copied, failed, dupsPerTarget };
  };

  const handleSubmitBulk = async () => {
    if (targetIds.length === 0) return;
    setIsRunning(true);
    const r = await runBulkForTargets(targetIds);
    setIsRunning(false);
    if (r.dupsPerTarget.length > 0) {
      setAccCopied(r.copied);
      setAccFailed(r.failed);
      setPendingBulkDups(r.dupsPerTarget);
      return;
    }
    finish(r.copied, r.failed);
  };

  const handleResolveBulkDups = async (mode) => {
    setIsRunning(true);
    const targets = pendingBulkDups.map((d) => d.targetProjectId);
    const r = await runBulkForTargets(targets, mode === "copy" ? { copyDuplicates: true } : { skipDuplicates: true });
    setIsRunning(false);
    finish(accCopied + r.copied, accFailed + r.failed);
  };

  const handlePrimary = () => (isBulk ? handleSubmitBulk() : handleSubmitSingle());
  const totalSingleDupCount = pendingSingleDups.length;
  const totalBulkDupCount = pendingBulkDups.reduce((s, d) => s + (d.duplicates?.length || 0), 0);

  return (
    <Modal open={open} onClose={handleClose} width={520}>
      <div style={{ padding: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}>
            <Copy size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: "var(--color-text-primary)" }}>
              {isBulk
                ? `Copy ${leadIds.length} lead${leadIds.length > 1 ? "s" : ""} to other projects`
                : "Copy lead to other projects"}
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)", lineHeight: 1.45 }}>
              Pick one or more destinations. A new independent lead is created in each. Status resets to <strong>Open</strong>. Both leads keep a reference to each other for your records.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              border: "none", background: "transparent", cursor: "pointer",
              color: "var(--color-text-secondary)", padding: 4,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {!showDupPrompt && (
          <>
            <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)", marginBottom: 6 }}>
              Destination projects {targetIds.length > 0 && <span style={{ color: "var(--crm-primary)" }}>· {targetIds.length} selected</span>}
            </label>
            <div style={{ position: "relative", marginBottom: 8 }}>
              <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search projects"
                disabled={isPending}
                style={{
                  width: "100%", height: 34, padding: "0 12px 0 30px",
                  borderRadius: 8, border: "1px solid var(--color-border-subtle)",
                  background: "var(--color-bg-surface)", color: "var(--color-text-primary)",
                  fontSize: 13, outline: "none",
                }}
              />
            </div>
            <div style={{
              maxHeight: 240, overflowY: "auto", border: "1px solid var(--color-border-subtle)",
              borderRadius: 8, background: "var(--color-bg-surface)",
            }}>
              {projectsLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
                  <Loader2 size={13} className="animate-spin" /> Loading projects…
                </div>
              )}
              {!projectsLoading && projectOptions.length === 0 && (
                <div style={{ padding: 12, fontSize: 13, color: "var(--color-text-secondary)" }}>
                  {q ? "No projects match your search." : "No other projects available."}
                </div>
              )}
              {projectOptions.map((p) => {
                const id = String(p._id);
                const checked = targetIds.includes(id);
                return (
                  <label key={id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 12px", borderBottom: "1px solid var(--color-border-subtle)",
                    cursor: "pointer",
                    background: checked ? "var(--crm-primary-soft)" : "transparent",
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleTarget(id)}
                      disabled={isPending}
                      style={{ width: 15, height: 15, cursor: "pointer", accentColor: "var(--crm-primary)" }}
                    />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {p.name}
                    </span>
                    {checked && <Check size={13} style={{ color: "var(--crm-primary)" }} />}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {pendingSingleDups.length > 0 && (
          <div style={{
            marginBottom: 4, padding: 12,
            border: "1px solid var(--crm-warning-soft, #FDE68A)",
            background: "var(--crm-warning-soft, #FEF3C7)",
            borderRadius: 8,
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <AlertTriangle size={15} style={{ color: "var(--crm-warning, #B45309)", flexShrink: 0, marginTop: 1 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--crm-warning, #92400E)" }}>
                  This lead already exists in {totalSingleDupCount} destination project{totalSingleDupCount > 1 ? "s" : ""}.
                </div>
                <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: 12, color: "var(--crm-warning, #92400E)" }}>
                  {pendingSingleDups.map((d) => (
                    <li key={d.targetProjectId} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <strong>{d.name}</strong>
                      {d.existingLead?.title ? ` — existing: ${d.existingLead.title}` : ""}
                    </li>
                  ))}
                </ul>
                {accCopied > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "var(--crm-warning, #92400E)" }}>
                    Already copied to {accCopied} project{accCopied > 1 ? "s" : ""}.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {pendingBulkDups.length > 0 && (
          <div style={{
            marginBottom: 4, padding: 12,
            border: "1px solid var(--crm-warning-soft, #FDE68A)",
            background: "var(--crm-warning-soft, #FEF3C7)",
            borderRadius: 8,
          }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <AlertTriangle size={15} style={{ color: "var(--crm-warning, #B45309)", flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--crm-warning, #92400E)" }}>
                  {totalBulkDupCount} lead{totalBulkDupCount > 1 ? "s" : ""} already exist across {pendingBulkDups.length} destination project{pendingBulkDups.length > 1 ? "s" : ""}.
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: "var(--crm-warning, #92400E)" }}>
                  {accCopied} copied so far. How should we handle the duplicates?
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={isPending}>Cancel</Button>

          {pendingSingleDups.length > 0 && (
            <>
              <Button variant="secondary" size="sm" onClick={handleSkipSingleDups} disabled={isPending}>
                Skip duplicates
              </Button>
              <Button variant="primary" size="sm" onClick={handleConfirmSingleDups} disabled={isPending}
                leftIcon={isPending ? <Loader2 size={13} className="animate-spin" /> : null}>
                Copy anyway
              </Button>
            </>
          )}

          {pendingBulkDups.length > 0 && (
            <>
              <Button variant="secondary" size="sm" onClick={() => handleResolveBulkDups("skip")} disabled={isPending}
                leftIcon={isPending ? <Loader2 size={13} className="animate-spin" /> : null}>
                Skip duplicates
              </Button>
              <Button variant="primary" size="sm" onClick={() => handleResolveBulkDups("copy")} disabled={isPending}
                leftIcon={isPending ? <Loader2 size={13} className="animate-spin" /> : null}>
                Copy all
              </Button>
            </>
          )}

          {!showDupPrompt && (
            <Button
              variant="primary" size="sm"
              onClick={handlePrimary}
              disabled={targetIds.length === 0 || isPending}
              leftIcon={isPending ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />}
            >
              {targetIds.length > 1 ? `Copy to ${targetIds.length} projects` : "Copy"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
